// Frontend wrapper for /api/shares (Step 11).
//
// PUT-first flow (mirrors artworksApi):
//   1. POST /api/shares { artworkId } -> { shareId, cardPutUrl, ... }
//   2. PUT cardPutUrl with the composed PNG blob (Content-Type image/png)

import {
  shareCreateResponseSchema,
  type ShareCreateResponse,
} from '@skena/schema'

interface ApiSuccess<T> {
  data: T
}
interface ApiError {
  error: { code: string; message: string; details?: unknown }
}
type ApiResult<T> = ApiSuccess<T> | ApiError

export class SharesApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message)
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as ApiResult<T>
  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : { code: 'INTERNAL', message: 'unexpected error' }
    throw new SharesApiError(err.code, err.message, res.status)
  }
  return json.data
}

export const sharesApi = {
  async create(artworkId: string): Promise<ShareCreateResponse> {
    const raw = await postJson<unknown>('/api/shares', { artworkId })
    return shareCreateResponseSchema.parse(raw)
  },
}

/** PUT the composed share-card PNG to R2. Same uploader pattern as artworks. */
export async function putShareCardR2(url: string, blob: Blob): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': 'image/png' },
  })
  if (!res.ok) {
    throw new SharesApiError(
      'R2_UPLOAD_FAILED',
      `share-card R2 PUT failed: ${res.status} ${res.statusText}`,
      res.status,
    )
  }
}
